import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getToken } from '../../../../features/token/tokenSaver';
import { getAdvertiserScreenInfo } from '../../../../services/getAdvertiserScreenInfo';
import { RingLoader } from 'react-spinners';
import './AdvertiserScreenInfo.css';
import { isScreenActive } from '../../../../utils/screenActiveStatus';

function formatDate(value) {
  if (!value) {
    return 'N/A';
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  return d.toLocaleString();
}

function displayValue(value) {
  if (value === undefined || value === null || String(value).trim() === '') {
    return 'N/A';
  }
  return String(value);
}

export default function AdvertiserScreenInfo() {
  const { screenId } = useParams();
  const navigate = useNavigate();
  const token = useSelector(getToken);
  const [screen, setScreen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [listingPhotoCacheBust, setListingPhotoCacheBust] = useState(0);

  useEffect(() => {
    const id = parseInt(screenId, 10);
    if (!id) {
      setError('Invalid screen');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getAdvertiserScreenInfo(token, id)
      .then((res) => {
        if (cancelled) {
          return;
        }
        const s = res.data?.screen;
        if (res.data?.success && s) {
          setScreen(s);
          setListingPhotoCacheBust(Date.now());
        } else {
          setError('Screen could not be loaded.');
          setScreen(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Screen could not be loaded.');
          setScreen(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, screenId]);

  const advertiserListingPhotoSrc = useMemo(() => {
    const url = screen?.listingPhotoUrl;
    if (!url || typeof url !== 'string') {
      return null;
    }
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}t=${listingPhotoCacheBust}`;
  }, [screen, listingPhotoCacheBust]);

  return (
    <div className="advertiserScreenInfoPage">
      <div className="advertiserScreenInfoHeader">
        <button
          type="button"
          className="advertiserScreenInfoBack"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <h1 className="advertiserScreenInfoTitle">Screen #{screenId}</h1>
      </div>

      {loading && (
        <div className="advertiserScreenInfoLoader">
          <RingLoader color="#C52F00" loading size={48} aria-label="Loading" />
        </div>
      )}

      {!loading && error && (
        <div className="advertiserScreenInfoError">{error}</div>
      )}

      {!loading && screen && (
        <div className="advertiserScreenInfoCard">
          <div className={`advertiserScreenInfoStatus ${isScreenActive(screen) ? 'online' : 'offline'}`}>
            <span className="advertiserScreenInfoStatusDot" />
            {isScreenActive(screen) ? 'Active (ad shown recently)' : 'Inactive / offline'}
          </div>

          {advertiserListingPhotoSrc && (
            <div className="advertiserScreenInfoPhotoWrap">
              <img
                className="advertiserScreenInfoListingPhoto"
                src={advertiserListingPhotoSrc}
                alt="Photo of this screen at the venue"
              />
            </div>
          )}

          <dl className="advertiserScreenInfoGrid">
            <dt>Address</dt>
            <dd>{displayValue(screen.address)}</dd>

            <dt>Brand</dt>
            <dd>{displayValue(screen.brand ?? screen.Brand)}</dd>

            <dt>Type</dt>
            <dd>{displayValue(screen.type ?? screen.Type)}</dd>

            <dt>Dimensions</dt>
            <dd>{displayValue(screen.dimensions ?? screen.Dimensions)}</dd>

            <dt>Location description</dt>
            <dd className="advertiserScreenInfoMultiline">
              {displayValue(screen.locationDescription ?? screen.LocationDescription)}
            </dd>

            <dt>Installed</dt>
            <dd>{formatDate(screen.installedDate ?? screen.InstalledDate)}</dd>

            <dt>Last active</dt>
            <dd>{screen.lastActive ? formatDate(screen.lastActive) : 'N/A'}</dd>

            <dt>Coordinates</dt>
            <dd>
              {screen.latitude != null && screen.longitude != null
                ? `${Number(screen.latitude).toFixed(6)}, ${Number(screen.longitude).toFixed(6)}`
                : 'N/A'}
            </dd>
          </dl>
        </div>
      )}
    </div>
  );
}
